/**
 * Cálculos educativos del Vanguard Market con aritmética entera (céntimos y centésimas de porcentaje).
 * No usan coma flotante para redondear.
 */

/** Convierte "12,50" o "12.5" en un entero escalado (12,50 con 2 decimales → 1250). null si no es válido. */
export function parseScaled(input: string, decimals: number): number | null {
  const s = input.trim().replace(',', '.')
  const re = new RegExp(`^\\d+(\\.\\d{0,${decimals}})?$`)
  if (!re.test(s)) return null
  const [int, frac = ''] = s.split('.')
  const n = Number(int) * 10 ** decimals + Number((frac + '0'.repeat(decimals)).slice(0, decimals) || '0')
  return Number.isSafeInteger(n) ? n : null
}

/** Formatea céntimos como "10,10". */
export const formatCents = (cents: number) => `${Math.floor(cents / 100)},${String(cents % 100).padStart(2, '0')}`

const ceilDiv = (a: number, b: number) => Math.floor((a + b - 1) / b)

export type RechargeResult = { ok: true; credits: number; cents: number } | { ok: false; error: string }

/** Recarga: USDT × 10. Importe positivo en múltiplos de 0,10 USDT. */
export function calcRecharge(usdt: string): RechargeResult {
  const cents = parseScaled(usdt, 2)
  if (cents === null || cents <= 0) return { ok: false, error: 'Escribe un importe positivo en USDT, por ejemplo 12,50.' }
  if (cents % 10 !== 0) return { ok: false, error: 'Usa múltiplos de 0,10 USDT para recibir créditos enteros.' }
  return { ok: true, cents, credits: cents / 10 }
}

export type SaleResult = { ok: true; fee: number; net: number; consumed: boolean } | { ok: false; error: string }

/** Venta: comisión = techo(precio × porcentaje / 100), neto = precio − comisión. */
export function calcSale(price: string, percent: string): SaleResult {
  const p = /^\d+$/.test(price.trim()) ? Number(price.trim()) : NaN
  if (!Number.isSafeInteger(p) || p <= 0) return { ok: false, error: 'El precio debe ser un número entero de créditos mayor que 0.' }
  const bp = parseScaled(percent, 2)
  if (bp === null || bp > 10000) return { ok: false, error: 'El porcentaje debe estar entre 0 y 100, con hasta dos decimales.' }
  const fee = ceilDiv(p * bp, 10000)
  const net = p - fee
  return { ok: true, fee, net, consumed: net <= 0 }
}

export type WithdrawResult = { ok: true; grossCents: number; feeCents: number; netCents: number } | { ok: false; error: string }

/** Retiro: créditos enteros ≥ 100; bruto = créditos / 10; comisión redondeada hacia arriba al céntimo. */
export function calcWithdraw(credits: string, percent: string, minCredits = 100): WithdrawResult {
  const c = /^\d+$/.test(credits.trim()) ? Number(credits.trim()) : NaN
  if (!Number.isSafeInteger(c) || c < minCredits) return { ok: false, error: `El retiro mínimo es de ${minCredits} créditos enteros (10,00 USDT brutos).` }
  const bp = parseScaled(percent, 2)
  if (bp === null || bp > 9999) return { ok: false, error: 'El porcentaje debe estar entre 0 y 99,99, con hasta dos decimales.' }
  const grossCents = c * 10
  const feeCents = ceilDiv(grossCents * bp, 10000)
  const netCents = grossCents - feeCents
  if (netCents <= 0) return { ok: false, error: 'Con ese porcentaje el neto no sería positivo.' }
  return { ok: true, grossCents, feeCents, netCents }
}
